import { useToast } from "@celebration/react";
import { ClbModal } from "@celebration/react/ClbModal/ClbModal";
import { ClbMultiSelect } from "@celebration/react/ClbMultiSelect/ClbMultiSelect";
import { ClbRadioButton } from "@celebration/react/ClbRadioButton/ClbRadioButton";
import { ClbSelect } from "@celebration/react/ClbSelect/ClbSelect";
import { Button, Drawer, Input } from "@components/index";
import { useJustification } from "@services/justification/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "../../DetailsGantt.module.scss";

type Props = {
  event: Sentinel.Timeline.Event;
  filterDate: Date;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  sourceFeature?: "Timeline" | "DelayManagement";
};
type IncidentReportValue = "yes" | "no" | "";
type FormState = {
  process: string;
  offendingSystem: Sentinel.Justification.OffendingSystem[];
  anotherOffendingSystem: string;
  relatedProblem: string;
  actionTaken: string;
  actionStatus: string;
  reportIncident: IncidentReportValue;
  incidentNumber: string;
};
const emptyForm = (process = ""): FormState => ({
  process,
  offendingSystem: [],
  anotherOffendingSystem: "",
  relatedProblem: "",
  actionTaken: "",
  actionStatus: "",
  reportIncident: "",
  incidentNumber: "",
});
const offendingSystems = [
  "Automacoes",
  "Hawk",
  "O9",
  "Processo",
  "Rede",
  "RPA",
  "SAPS4",
  "Sirius",
  "TMS",
  "Other",
] as const satisfies ReadonlyArray<Sentinel.Justification.OffendingSystem>;
const offendingSystemLabels: Partial<
  Record<Sentinel.Justification.OffendingSystem, string>
> = { Automacoes: "Automações", Other: "Outro", SAPS4: "SAP S4" };
const systems = offendingSystems.map((id) => ({
  id,
  label: offendingSystemLabels[id] ?? id,
}));
const statuses = [
  { id: "on-hold", label: "Em espera", value: "Em espera" },
  { id: "doing", label: "Em andamento", value: "Em andamento" },
  { id: "concluded", label: "Concluído", value: "Concluído" },
];
const statusApi: Record<string, Sentinel.Justification.ActionStatus> = {
  "Em espera": "OnHold",
  "Em andamento": "Doing",
  Concluído: "Concluded",
};
const statusLabel: Record<Sentinel.Justification.ActionStatus, string> = {
  OnHold: "Em espera",
  Doing: "Em andamento",
  Concluded: "Concluído",
};
const incidents = [
  { id: "yes", label: "Sim, houve um incidente no processo", value: "yes" },
  { id: "no", label: "Não, não houve incidente no processo", value: "no" },
];
const dateString = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
const isConcludedStatus = (status?: string) =>
  status?.toUpperCase() === "CONCLUDED";
const JUSTIFICATION_TEXT_MAX_LENGTH = 255;

export default function Justification({
  event,
  filterDate,
  isOpen,
  onClose,
  onSaved,
  sourceFeature = "Timeline",
}: Props) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [isReopened, setIsReopened] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const { createJustification, getLastJustification } = useJustification();
  const toast = useToast();
  const queryClient = useQueryClient();
  const options = useMemo(
    () =>
      systems.map((option) => ({
        ...option,
        checked: form.offendingSystem.includes(option.id),
      })),
    [form.offendingSystem]
  );

  useEffect(() => {
    if (!isOpen) return;
    setIsReopened(false);
    setForm(emptyForm(event.displayName ?? ""));
    void getLastJustification(filterDate, event.id ?? 0)
      .then((last) => {
        if (!last) return;
        setForm({
          process: event.displayName ?? "",
          offendingSystem: last.offendingSystems ?? [],
          anotherOffendingSystem: last.anotherOffendingSystem ?? "",
          relatedProblem: last.relatedProblem ?? "",
          actionTaken: last.actionPerformed ?? "",
          actionStatus: statusLabel[last.actionStatus] ?? "",
          reportIncident: last.incidentNumber ? "yes" : "no",
          incidentNumber: last.incidentNumber ?? "",
        });
      })
      .catch(() => undefined);
  }, [event.displayName, event.id, filterDate, getLastJustification, isOpen]);

  const update = (change: Partial<FormState>) =>
    setForm((current) => ({ ...current, ...change }));
  const save = useCallback(async () => {
    const isCompleted =
      sourceFeature === "DelayManagement"
        ? isConcludedStatus(event.status)
        : event.hasCompleteJustification === true;

    if (isCompleted && !isReopened) return;
    const messages = [
      !form.process || !event.id ? "Processo é obrigatório" : "",
      !form.relatedProblem.trim() ? "Problema relacionado é obrigatório" : "",
      !form.actionStatus ? "Status da ação é obrigatório" : "",
    ].filter(Boolean);
    messages.forEach((description) =>
      toast.openToast({
        title: "Validação",
        description,
        type: "warning",
        autoClose: true,
      })
    );
    if (messages.length || !event.id) return;
    try {
      await createJustification(event.id, {
        timelineJustificationDate: dateString(filterDate),
        offendingSystems: form.offendingSystem,
        ...(form.offendingSystem.includes("Other")
          ? { anotherOffendingSystem: form.anotherOffendingSystem }
          : {}),
        relatedProblem: form.relatedProblem,
        actionPerformed: form.actionTaken,
        actionStatus: statusApi[form.actionStatus],
        incidentNumber:
          form.reportIncident === "yes" ? form.incidentNumber : null,
        sourceFeature,
      });
      toast.openToast({
        title: "Justificativa adicionada!",
        description:
          "O sistema informa ao usuário que a justificativa foi adicionada para o processo selecionado.",
        type: "positive",
        autoClose: true,
      });
      await queryClient.cancelQueries({ queryKey: ["timeline"] });
      await queryClient.refetchQueries({
        queryKey: ["timeline", filterDate],
        exact: true,
      });
      await queryClient.refetchQueries({
        queryKey: ["timeline", "gantt", filterDate],
        exact: true,
      });
      void queryClient.invalidateQueries({ queryKey: ["neg-execution"] });
      onSaved?.();
      onClose();
    } catch {
      toast.openToast({
        title: "Justificativa não adicionada!",
        description: "Não foi possível adicionar a justificativa ao processo.",
        type: "negative",
        autoClose: true,
      });
    }
  }, [
    createJustification,
    event,
    filterDate,
    form,
    isReopened,
    onClose,
    onSaved,
    queryClient,
    sourceFeature,
    toast,
  ]);

  const isReadOnly =
    sourceFeature === "DelayManagement"
      ? isConcludedStatus(event.status) && !isReopened
      : event.hasCompleteJustification === true && !isReopened;

  return (
    <>
      <Drawer
        overlay
        title="Justificar atraso"
        description="Informe a tratativa do atraso no processo utilizando os campos abaixo:"
        isOpen={isOpen}
        onClose={onClose}
        idUsedToOpenDrawer="open-drawer-justification"
        customFooter={() => (
          <div className={styles["justification-footer"]}>
            <Button
              label="Cancelar"
              buttonStyle="tertiary"
              size="md"
              onClick={onClose}
            />
            {isReadOnly ? (
              <Button
                label="Reabrir"
                buttonStyle="primary"
                size="md"
                onClick={() => setShowReopenModal(true)}
              />
            ) : (
              <Button
                label="Enviar justificativa"
                buttonStyle="primary"
                size="md"
                onClick={save}
              />
            )}
          </div>
        )}
      >
        <form className={styles["justification-form"]}>
          <ClbSelect
            label="Processo"
            placeholder={
              form.process || "Selecione o processo que deseja detalhar"
            }
            size="lg"
            value={form.process}
            options={[
              { id: form.process, label: form.process, value: form.process },
            ]}
            disabled
            required
          />
          <ClbMultiSelect
            disabled={isReadOnly}
            label="Sistema ofensor"
            placeholder="Selecione o sistema ofensor"
            size="lg"
            options={options}
            i18n={{
              selectAllOptions: "Selecionar todos",
              labelTag: "selecionado(s)",
              noOptionsLabel: "Nenhuma opção disponível",
            }}
            onChange={(e) => {
              const offendingSystem = e.selectedOptions.map(
                (o) => o.id as Sentinel.Justification.OffendingSystem
              );
              update({
                offendingSystem,
                ...(offendingSystem.includes("Other")
                  ? {}
                  : { anotherOffendingSystem: "" }),
              });
            }}
          />
          {form.offendingSystem.includes("Other") && (
            <Input.Text
              disabled={isReadOnly}
              label="Outro sistema ofensor"
              placeholder="Informe o sistema ofensor"
              size="lg"
              maxLength={JUSTIFICATION_TEXT_MAX_LENGTH}
              showCharCount
              value={form.anotherOffendingSystem}
              onChange={(e) =>
                update({ anotherOffendingSystem: e.target.value })
              }
            />
          )}
          <Input.Text
            disabled={isReadOnly}
            label="Problema relacionado"
            placeholder="Informe o problema relacionado"
            size="lg"
            maxLength={JUSTIFICATION_TEXT_MAX_LENGTH}
            showCharCount
            value={form.relatedProblem}
            required
            onChange={(e) => update({ relatedProblem: e.target.value })}
          />
          <Input.Text
            disabled={isReadOnly}
            label="Ação realizada"
            placeholder="Informe a ação realizada"
            size="lg"
            maxLength={JUSTIFICATION_TEXT_MAX_LENGTH}
            showCharCount
            value={form.actionTaken}
            onChange={(e) => update({ actionTaken: e.target.value })}
          />
          <ClbSelect
            disabled={isReadOnly}
            label="Status da ação"
            placeholder={form.actionStatus || "Selecione o status da ação"}
            size="lg"
            value={form.actionStatus}
            options={statuses}
            required
            onChange={(e) => update({ actionStatus: e.target.value })}
          />
          <div className={styles["justification-radio-group"]}>
            <div className={styles["justification-field-label"]}>
              Reportar incidente no processo?
            </div>
            <ClbRadioButton
              disabled={isReadOnly}
              name="report-incident"
              selectedValue={form.reportIncident}
              options={incidents}
              onChange={(e) =>
                update({
                  reportIncident: e.value as IncidentReportValue,
                  incidentNumber: e.value === "yes" ? form.incidentNumber : "",
                })
              }
            />
          </div>
          {form.reportIncident === "yes" && (
            <Input.Text
              disabled={isReadOnly}
              label="Informe o número do incidente"
              placeholder="Informe o número do incidente"
              size="lg"
              maxLength={JUSTIFICATION_TEXT_MAX_LENGTH}
              showCharCount
              value={form.incidentNumber}
              onChange={(e) => update({ incidentNumber: e.target.value })}
            />
          )}
        </form>
      </Drawer>
      <ClbModal
        open={showReopenModal}
        title="Reabrir justificativa"
        description="Essa justificativa já foi finalizada, deseja reabrir?"
        itemsFooter={[
          {
            key: "yes",
            label: "Sim",
            type: "button",
            buttonStyle: "primary",
            size: "sm",
            onClick: () => {
              setIsReopened(true);
              setShowReopenModal(false);
            },
          },
          {
            key: "no",
            label: "Não",
            type: "button",
            buttonStyle: "secondary",
            size: "sm",
            onClick: () => setShowReopenModal(false),
          },
        ]}
        customBody={<></>}
        onClose={() => setShowReopenModal(false)}
      />
    </>
  );
}
